import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-24">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-8 text-earth-800">Privacy Policy</h1>
          <div className="prose prose-earth max-w-none">
            <p className="text-lg text-earth-600 mb-6">
              Resonow ("we", "our", "us") is committed to protecting your privacy. This Privacy Notice explains how we collect, use, disclose, and safeguard your information when you use our platform and integrated services.
            </p>

            <div className="space-y-6 text-earth-600">
              <p>Last updated: September 14, 2025</p>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">1. Information We Collect</h2>
                <h3 className="text-xl font-semibold text-earth-700 mb-2">Information You Provide</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Account Information:</strong> Name, email, password, phone (optional), birth date (optional), gender (optional), country/region.</li>
                  <li><strong>Profile Information:</strong> Profile picture, bio, qualifications, services, availability, address.</li>
                  <li><strong>Booking Information:</strong> Service details, session time, price, communication records.</li>
                  <li><strong>Payment Information:</strong> Processed securely via Stripe. We do not store full card details.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">2. How We Use Your Information</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide and maintain our platform services.</li>
                  <li>Enable scheduling and management of client–practitioner sessions.</li>
                  <li>Process secure payments and bookings.</li>
                  <li>Communicate service updates and confirmations.</li>
                  <li>Verify practitioner qualifications.</li>
                  <li>Improve platform performance, security, and user experience.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">3. Information Sharing</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Between Clients and Practitioners:</strong> For service delivery and scheduling.</li>
                  <li><strong>With Service Providers:</strong> (e.g., Stripe for payments, Google APIs for meetings).</li>
                  <li><strong>Legal Obligations:</strong> When required by law, regulation, or valid legal request.</li>
                  <li><strong>Business Transfers:</strong> In case of a merger, acquisition, or similar event.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">4. Google Meet Integration</h2>
                <h3 className="text-xl font-semibold text-earth-700 mb-2">Data We Access and Why</h3>
                <p className="text-earth-600 mb-4">
                  When practitioners connect their Google accounts via OAuth, we obtain:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>Access and refresh tokens</li>
                  <li>Token expiration time</li>
                  <li>Limited Google Calendar/Meet metadata (event ID, meeting link, start time)</li>
                </ul>
                <p className="text-earth-600">
                  This data is used exclusively to schedule and manage sessions on behalf of the practitioner within our platform.
                </p>
                
                <h3 className="text-xl font-semibold text-earth-700 mb-2 mt-6">How We Use and Protect This Data</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Tokens are securely stored using encryption-at-rest and transmitted only over HTTPS.</li>
                  <li>Tokens are used solely by our server-side processes to create and manage Google Meet sessions.</li>
                  <li>We do not share Google OAuth tokens or meeting data with third parties.</li>
                  <li>Practitioners may revoke access at any time by disconnecting Google from their account, which revokes our OAuth permissions.</li>
                </ul>

                <h3 className="text-xl font-semibold text-earth-700 mb-2 mt-6">Data Retention and Deletion</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>OAuth tokens are retained only while the integration is active and refreshed or revoked automatically.</li>
                  <li>Meeting metadata (e.g., join link, meeting ID, session time) may be stored for operational, billing, and audit purposes.</li>
                  <li>Upon disconnection, we immediately cease access to Google account data and delete or de-identify retained tokens in accordance with our retention policies and legal obligations.</li>
                  <li>Practitioners can request deletion of personal information at any time by contacting info@resonow.org.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">5. Data Protection and Security</h2>
                <p className="text-earth-600 mb-4">
                  We apply industry-standard technical and organizational safeguards to protect sensitive data:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Data Minimization:</strong> We collect and retain only the minimum necessary information to deliver services.</li>
                  <li><strong>Encryption:</strong> All personal and sensitive data is encrypted in transit (TLS 1.2/1.3) and at rest (AES-256).</li>
                  <li><strong>Access Controls:</strong> Role-based access (RBAC) ensures only authorized staff can access sensitive data. All administrative access requires multi-factor authentication (MFA).</li>
                  <li><strong>Monitoring & Auditing:</strong> We log and monitor access to sensitive systems. Regular audits and penetration tests are conducted to ensure security controls remain effective.</li>
                  <li><strong>Third-Party Security:</strong> Stripe handles all payment processing in compliance with PCI-DSS Level 1. Google Meet data is handled under Google's security and privacy framework.</li>
                  <li><strong>Compliance:</strong> We align our practices with GDPR, CCPA, and other applicable data protection laws. Users can exercise rights to access, deletion, correction, and portability of their data.</li>
                  <li><strong>No Sale of Personal Data:</strong> We do not sell or rent personal data to third parties for marketing or advertising purposes.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">6. Your Rights</h2>
                <p className="text-earth-600 mb-4">
                  Subject to applicable law, you have the right to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access, correct, or update your personal information.</li>
                  <li>Request deletion of your personal data.</li>
                  <li>Restrict or object to certain types of processing.</li>
                  <li>Request data portability.</li>
                  <li>Withdraw consent at any time (e.g., by disconnecting your Google account).</li>
                </ul>
                <p className="text-earth-600 mt-4">
                  You may exercise these rights by contacting info@resonow.org. We may verify your identity before responding.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">7. Contact Us</h2>
                <p>For privacy-related questions, please contact us at:</p>
                <p><strong>Email:</strong> info@resonow.org</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;